import React, { useState } from 'react';
import { User, Edit3, Check, X, Mail, Phone } from 'lucide-react';
import Card from '../../../../components/ui/card';
import Button from '../../../../components/ui/button';
import Input from '../../../../components/ui/input';
import { Textarea } from '../../../../components/ui/textarea';

const PersonalInfoCard = ({ client, clientDataHook }) => {
	const { updateField, isEditMode, toggleEditMode, cancelEdit } =
		clientDataHook;
	const [editValues, setEditValues] = useState({});

	// Phone validation function
	const validatePhone = (phone) => {
		if (!phone || phone.trim() === '') return true; // Allow empty phone (optional field)
		// Basic phone validation - allows various formats
		const phoneRegex = /^[\+]?[1-9][\d]{0,15}$|^[\+]?[(]?[\d\s\-\(\)\.]{10,}$/;
		return phoneRegex.test(phone.replace(/[\s\-\(\)\.]/g, ''));
	};

	const fields = [
		{
			key: 'firstName',
			label: 'First Name',
			type: 'text',
			value: client?.firstName || '',
			required: true,
			icon: User,
			placeholder: 'Enter first name',
		},
		{
			key: 'lastName',
			label: 'Last Name',
			type: 'text',
			value: client?.lastName || '',
			required: true,
			icon: User,
			placeholder: 'Enter last name',
		},
		{
			key: 'designations',
			label: 'Designation',
			type: 'text',
			value: client?.designations || '',
			placeholder: 'e.g., REALTOR®, Broker, Property Manager',
		},
		{
			key: 'licenseNumber',
			label: 'License/DRE #',
			type: 'text',
			value: client?.licenseNumber || '',
			placeholder: 'License or DRE number',
		},
		{
			key: 'certifications',
			label: 'Certifications',
			type: 'text',
			value: client?.certifications || '',
			placeholder: 'Professional certifications...',
		},
		{
			key: 'email',
			label: 'Email',
			type: 'email',
			value: client?.email || '',
			required: true,
			icon: Mail,
			placeholder: 'Enter email address',
		},
		{
			key: 'phone',
			label: 'Phone',
			type: 'tel',
			value: client?.phone || '',
			icon: Phone,
			placeholder: 'Enter phone number (e.g., +1 555-123-4567)',
			validation: 'phone',
		},
	];

	const handleEdit = (fieldKey) => {
		setEditValues({
			...editValues,
			[fieldKey]: fields.find((f) => f.key === fieldKey)?.value || '',
		});
		toggleEditMode(fieldKey);
	};

	const handleSave = async (fieldKey) => {
		const success = await updateField(fieldKey, editValues[fieldKey]);
		if (success) {
			setEditValues({ ...editValues, [fieldKey]: '' });
		}
	};

	const handleCancel = (fieldKey) => {
		setEditValues({ ...editValues, [fieldKey]: '' });
		cancelEdit(fieldKey);
	};

	const renderField = (field) => {
		const isEditing = isEditMode[field.key];
		const editValue = editValues[field.key] ?? field.value;
		const hasValue = field.value && field.value.trim() !== '';

		// Validation logic
		const isValidField = () => {
			if (field.required && !editValue.trim()) return false;
			if (
				field.validation === 'phone' &&
				editValue.trim() &&
				!validatePhone(editValue)
			)
				return false;
			return true;
		};

		// Conditional styling: white background for filled fields, gray for unfilled
		const cardBgClass = hasValue ? 'bg-white shadow-sm' : 'bg-stone-50/50';

		return (
			<div
				key={field.key}
				className={`${cardBgClass} border border-gray-100 rounded-lg p-3 sm:p-4 transition-all duration-200 hover:border-primary/30`}>
				<div className='flex items-start justify-between'>
					<div className='flex-1'>
						<label className='flex items-center text-xs font-montserrat-medium uppercase tracking-wide text-gray-400 mb-1.5'>
							{field.icon && <field.icon className='w-3.5 h-3.5 mr-1.5 text-primary/30' />}
							{field.label}
							{field.required && <span className='text-red-500 ml-1'>*</span>}
						</label>

						{isEditing ? (
							<div className='space-y-3'>
								{field.type === 'textarea' ? (
									<Textarea
										value={editValue}
										onChange={(e) =>
											setEditValues({
												...editValues,
												[field.key]: e.target.value,
											})
										}
										placeholder={field.placeholder}
										className='w-full'
										rows={3}
									/>
								) : (
									<Input
										value={editValue}
										onChange={(e) =>
											setEditValues({
												...editValues,
												[field.key]: e.target.value,
											})
										}
										placeholder={field.placeholder}
										className={`w-full ${
											field.validation === 'phone' &&
											editValue.trim() &&
											!validatePhone(editValue)
												? 'border-red-500 focus:border-red-500'
												: ''
										}`}
									/>
								)}

								{/* Phone validation error message */}
								{field.validation === 'phone' &&
									editValue.trim() &&
									!validatePhone(editValue) && (
										<div className='text-red-500 text-xs mt-1'>
											Please enter a valid phone number
										</div>
									)}

								<div className='flex items-center space-x-2'>
									<Button
										size='sm'
										onClick={() => handleSave(field.key)}
										disabled={!isValidField()}>
										<Check className='w-4 h-4 mr-1' />
										Save
									</Button>
									<Button
										size='sm'
										variant='outline'
										onClick={() => handleCancel(field.key)}>
										<X className='w-4 h-4 mr-1' />
										Cancel
									</Button>
								</div>
							</div>
						) : (
							<div className='text-sm font-montserrat-medium text-gray-900'>
								{field.value ? (
									field.type === 'textarea' ? (
										<div className='whitespace-pre-wrap'>{field.value}</div>
									) : (
										field.value
									)
								) : (
									<span className='text-gray-300 italic font-montserrat-regular'>
										Not provided
									</span>
								)}
							</div>
						)}
					</div>

					{!isEditing && (
						<Button
							size='sm'
							variant='ghost'
							onClick={() => handleEdit(field.key)}
							aria-label={`Edit ${field.label}`}
							className='ml-2 text-gray-300 hover:text-primary flex-shrink-0 transition-colors duration-200'>
							<Edit3 className='w-4 h-4' />
						</Button>
					)}
				</div>
			</div>
		);
	};

	return (
		<Card className='border-l-2 border-l-primary/20'>
			<Card.Header className='border-b border-gray-100'>
				<Card.Title className='flex items-center text-sm sm:text-base font-glacial uppercase tracking-wider text-gray-900'>
					<User className='w-4 h-4 mr-2 text-primary' />
					Personal Information
				</Card.Title>
			</Card.Header>
			<Card.Content>
				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					{fields.map(renderField)}
				</div>
			</Card.Content>
		</Card>
	);
};

export default PersonalInfoCard;
